import { useCallback, useEffect, useRef, useState } from "react";

const SEGMENT_MS = 30000;
const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
export const pickMime = () => MIMES.find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || "";
export const extFor = (type = "") => (type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm");

const micError = (e) => {
  if (e?.name === "NotAllowedError") return "Accès au micro refusé. Autorise le micro dans les réglages de ton navigateur, puis réessaie.";
  if (e?.name === "NotFoundError") return "Aucun micro détecté sur cet appareil.";
  if (e?.name === "NotReadableError") return "Le micro est déjà utilisé par une autre application.";
  return "Impossible d'accéder au micro.";
};

export function useRecorder({ onSegment }) {
  const [state, setState] = useState("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const streamRef = useRef(null), recRef = useRef(null), ctxRef = useRef(null), analyserRef = useRef(null);
  const segTimer = useRef(null), tick = useRef(null), indexRef = useRef(0), wakeRef = useRef(null);
  const onSegmentRef = useRef(onSegment);
  onSegmentRef.current = onSegment;
  const supported = typeof window !== "undefined" && !!(navigator.mediaDevices?.getUserMedia && window.MediaRecorder);

  const startSegment = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime, audioBitsPerSecond: 32000 } : undefined);
    const parts = [], started = Date.now(), index = indexRef.current++;
    rec.ondataavailable = (e) => e.data?.size && parts.push(e.data);
    rec.onstop = () => {
      const type = rec.mimeType || mime || "audio/webm";
      const blob = new Blob(parts, { type });
      const dur = (Date.now() - started) / 1000;
      if (blob.size > 0 && dur > 0.8) onSegmentRef.current(blob, dur, index, type);
    };
    rec.start(1000);
    recRef.current = rec;
    segTimer.current = setTimeout(() => {
      if (recRef.current === rec && rec.state === "recording") {
        rec.stop();
        startSegment();
      }
    }, SEGMENT_MS);
  }, []);

  const closeMic = useCallback(() => {
    clearTimeout(segTimer.current);
    clearInterval(tick.current);
    const rec = recRef.current;
    recRef.current = null;
    if (rec && rec.state !== "inactive") rec.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    ctxRef.current?.close().catch(() => {});
    ctxRef.current = null;
    analyserRef.current = null;
    wakeRef.current?.release?.().catch(() => {});
    wakeRef.current = null;
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      ctxRef.current = ctx;
      analyserRef.current = analyser;
      stream.getAudioTracks()[0].onended = () => {
        closeMic();
        setState("paused");
        setError("Le micro a été interrompu (déconnecté ou utilisé par une autre application). Tes segments déjà enregistrés sont conservés : appuie sur Reprendre.");
      };
      try { wakeRef.current = await navigator.wakeLock?.request("screen"); } catch { /* optional */ }
    } catch (e) {
      setError(micError(e));
      return false;
    }
    startSegment();
    setState("recording");
    tick.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    return true;
  }, [startSegment, closeMic]);

  const pause = useCallback(() => { closeMic(); setState("paused"); }, [closeMic]);
  const stop = useCallback(() => { closeMic(); setState("stopped"); }, [closeMic]);

  useEffect(() => () => closeMic(), [closeMic]);

  return { state, elapsed, error, setError, analyserRef, start, pause, resume: start, stop, supported };
}
